import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  hasAnsweredConsent,
  setAnalyticsConsent,
} from '@/lib/analytics';
import { ShieldCheck, X } from 'lucide-react';

/**
 * Opt-in analytics consent banner.
 *
 * Shown once until the user chooses. If they decline (or ignore via the X),
 * nothing is ever reported. Only anonymous, aggregate usage counts are
 * collected on accept — never email content, recipients, or personal data.
 */
export function ConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Delay slightly so it doesn't fight the initial paint.
    const t = setTimeout(() => {
      if (!hasAnsweredConsent()) setVisible(true);
    }, 800);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  const decide = (granted: boolean) => {
    setAnalyticsConsent(granted);
    setVisible(false);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-4">
      <div className="mx-auto flex max-w-3xl flex-col gap-3 rounded-xl border bg-card/95 p-4 shadow-lg backdrop-blur sm:flex-row sm:items-center">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <p className="text-sm text-muted-foreground">
            Help improve BCH Signature with{' '}
            <span className="font-medium text-foreground">
              anonymous usage stats
            </span>
            ? We only count things like templates used and signatures copied —
            never your email content, contacts, or any personal data. You can
            change this anytime.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2 sm:ml-auto">
          <Button variant="ghost" size="sm" onClick={() => decide(false)}>
            No thanks
          </Button>
          <Button size="sm" onClick={() => decide(true)}>
            Allow
          </Button>
          <button
            onClick={() => decide(false)}
            className="ml-1 rounded-md p-1 text-muted-foreground hover:bg-muted"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
